import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { useAuth } from '../../../contexts/AuthContext';
import { openWeeklyMaintenanceSchedulePrint } from '../utils/scheduleWeeklyMaintenancePrint';

type ScheduleFilterFields = {
  /** Service-area ids/names selected on the Schedule page (multiselect; empty = all). */
  serviceAreas: string[];
  /** Foreman-area ids selected on the Schedule page (multiselect; empty = all). */
  foremanAreas: string[];
  startDate: string;
  endDate: string;
};

interface PrintScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  appliedFilters: ScheduleFilterFields;
  serviceAreas: Array<{ id: number; name: string }>;
  foremanAreas: Array<{
    id: number;
    name: string;
    serviceAreaId: number;
    serviceAreaName: string;
  }>;
}

type Step = 'choose' | 'weekly';

function addDaysToIsoDate(isoDate: string, days: number): string {
  if (!isoDate) return '';
  const [yearRaw, monthRaw, dayRaw] = isoDate.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return '';

  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const PrintScheduleModal: React.FC<PrintScheduleModalProps> = ({
  isOpen,
  onClose,
  appliedFilters,
  serviceAreas,
  foremanAreas,
}) => {
  const { currentUser } = useAuth();
  const [step, setStep] = useState<Step>('choose');
  const [weeklyForeman, setWeeklyForeman] = useState('');
  const [weeklyStart, setWeeklyStart] = useState('');
  const [weeklyEnd, setWeeklyEnd] = useState('');
  const [isWeeklyEndManuallyEdited, setIsWeeklyEndManuallyEdited] = useState(false);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklyErrors, setWeeklyErrors] = useState<{ foreman?: string; start?: string; end?: string }>({});
  // A failed action (chart not ready, generate failed) shows inline in the modal (STATE-06).
  const [actionError, setActionError] = useState<string | null>(null);

  const wasOpenRef = useRef(false);

  const goToStep = useCallback((next: 'choose' | 'weekly') => {
    setStep(next);
    setActionError(null);
    setWeeklyErrors({});
  }, []);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      goToStep('choose');
      // The weekly report covers exactly one foreman area, so a multi-selection cannot be carried
      // over as-is; preselect only when the page is filtered to a single area.
      const selectedForemanAreas = Array.isArray(appliedFilters.foremanAreas)
        ? appliedFilters.foremanAreas
        : [];
      setWeeklyForeman(selectedForemanAreas.length === 1 ? selectedForemanAreas[0] : '');
      const startFromSchedule = appliedFilters.startDate || '';
      setWeeklyStart(startFromSchedule);
      /** Weekly maintenance uses a 1-week window from start, not the Schedule page end date (often 2 weeks). */
      setWeeklyEnd(startFromSchedule ? addDaysToIsoDate(startFromSchedule, 7) : '');
      setIsWeeklyEndManuallyEdited(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, appliedFilters, goToStep]);

  const handleWeeklyStartChange = useCallback((value: string) => {
    setWeeklyStart(value);
    setWeeklyErrors((prev) => ({ ...prev, start: undefined, ...(value && !isWeeklyEndManuallyEdited ? { end: undefined } : {}) }));
    if (!value || isWeeklyEndManuallyEdited) return;
    setWeeklyEnd(addDaysToIsoDate(value, 7));
  }, [isWeeklyEndManuallyEdited]);

  const handleWeeklyEndChange = useCallback((value: string) => {
    setWeeklyEnd(value);
    setIsWeeklyEndManuallyEdited(true);
    setWeeklyErrors((prev) => ({ ...prev, end: undefined }));
  }, []);

  /** Joined so the memo below depends on selection *content*, not on a fresh array identity. */
  const appliedServiceAreasKey = (Array.isArray(appliedFilters.serviceAreas)
    ? appliedFilters.serviceAreas
    : []
  ).join(',');

  const foremanOptions = useMemo(() => {
    const list = Array.isArray(foremanAreas) ? foremanAreas : [];
    const selectedServiceAreas = new Set(appliedServiceAreasKey.split(',').filter((s) => s.length > 0));
    let base =
      selectedServiceAreas.size === 0
        ? list
        : list.filter((fa) => selectedServiceAreas.has(String(fa.serviceAreaId)));
    if (weeklyForeman && !base.some((fa) => String(fa.id) === weeklyForeman)) {
      const extra = list.find((fa) => String(fa.id) === weeklyForeman);
      if (extra) base = [extra, ...base];
    }
    return base;
  }, [foremanAreas, appliedServiceAreasKey, weeklyForeman]);

  const handlePrintCurrentSchedule = useCallback(() => {
    const w = window as unknown as { __printSchedulePdf?: () => void };
    if (typeof w.__printSchedulePdf === 'function') {
      w.__printSchedulePdf();
      onClose();
    } else {
      setActionError('The schedule chart is still loading. Try again in a moment.');
    }
  }, [onClose]);

  const handleWeeklyGenerate = useCallback(async () => {
    const fa = weeklyForeman.trim();
    const start = weeklyStart.trim();
    const end = weeklyEnd.trim();
    setActionError(null);

    // Each problem shows under its field (FORM-01)
    const errors: { foreman?: string; start?: string; end?: string } = {};
    if (!fa) {
      errors.foreman = 'Foreman area required';
    } else if (!foremanOptions.some((x) => String(x.id) === fa)) {
      errors.foreman = 'Choose a foreman area from the list';
    }
    if (!start) errors.start = 'Start date required';
    if (!end) errors.end = 'End date required';
    if (errors.foreman || errors.start || errors.end) {
      setWeeklyErrors(errors);
      const firstId = errors.foreman ? 'printWeeklyForeman' : errors.start ? 'printWeeklyStart' : 'printWeeklyEnd';
      document.getElementById(firstId)?.focus();
      return;
    }

    let dateFrom = start;
    let dateTo = end;
    if (dateFrom > dateTo) {
      const t = dateFrom;
      dateFrom = dateTo;
      dateTo = t;
    }

    const faMeta = foremanOptions.find((x) => String(x.id) === fa);
    if (!faMeta) return; // ruled out above

    const foremanAreaText = (faMeta.name || '').trim() || '–';
    const serviceAreaNameResolved =
      (faMeta.serviceAreaName && String(faMeta.serviceAreaName).trim()) ||
      serviceAreas.find((s) => s.id === faMeta.serviceAreaId)?.name ||
      '–';

    const scheduledBy = [currentUser?.name, currentUser?.lastName].filter(Boolean).join(' ').trim() || '–';

    setWeeklyLoading(true);
    try {
      await openWeeklyMaintenanceSchedulePrint({
        /** Scope tasks to this foreman’s service area so the header and API match. */
        serviceArea: String(faMeta.serviceAreaId),
        foremanArea: fa,
        dateFrom,
        dateTo,
        managerAreaName: foremanAreaText,
        serviceAreaName: serviceAreaNameResolved,
        scheduledBy,
      });
      onClose();
    } catch (err) {
      console.error('Failed to generate weekly maintenance schedule:', err);
      setActionError("Couldn't generate the weekly maintenance schedule. Try again.");
    } finally {
      setWeeklyLoading(false);
    }
  }, [
    weeklyForeman,
    weeklyStart,
    weeklyEnd,
    foremanOptions,
    serviceAreas,
    currentUser?.name,
    currentUser?.lastName,
    onClose,
  ]);

  const title = step === 'choose' ? 'Print schedule' : 'Weekly maintenance schedule';

  const footer =
    step === 'choose' ? (
      <>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </>
    ) : (
      <>
        <Button variant="outline-secondary" onClick={() => goToStep('choose')} disabled={weeklyLoading}>
          Back
        </Button>
        <Button variant="primary" onClick={() => void handleWeeklyGenerate()} disabled={weeklyLoading}>
          {weeklyLoading ? (
            <>
              <Spinner animation="border" size="sm" className="tw-mr-2" />
              Generating…
            </>
          ) : (
            'Generate'
          )}
        </Button>
      </>
    );

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} title={title} size="medium" footer={footer}>
      {actionError && (
        <Alert variant="danger" role="alert" className="tw-mb-3">
          {actionError}
        </Alert>
      )}
      {step === 'choose' ? (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <p className="tw-mb-0 tw-text-content-muted tw-text-sm">Choose which schedule you want to generate.</p>
          <div className="tw-grid tw-gap-2">
            <Button variant="primary" className="tw-text-left tw-py-3" onClick={handlePrintCurrentSchedule}>
              <span className="tw-font-semibold tw-block">Schedule</span>
              <span className="tw-text-sm tw-opacity-75">Print the current chart (same as before)</span>
            </Button>
            <Button variant="outline-primary" className="tw-text-left tw-py-3" onClick={() => goToStep('weekly')}>
              <span className="tw-font-semibold tw-block">Weekly maintenance schedule</span>
              <span className="tw-text-sm tw-text-content-muted">Routine tasks for a foreman area and date range</span>
            </Button>
          </div>
        </div>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <p className="tw-mb-0 tw-text-content-muted tw-text-sm">
            Select a foreman area and date range. Values default from your applied filters when set; otherwise choose
            them here.
          </p>
          <Form.Group controlId="printWeeklyForeman">
            <Form.Label className="tw-text-sm">Foreman area</Form.Label>
            <Form.Select
              value={weeklyForeman}
              onChange={(e) => {
                setWeeklyForeman(e.target.value);
                setWeeklyErrors((prev) => ({ ...prev, foreman: undefined }));
              }}
              disabled={weeklyLoading}
              isInvalid={!!weeklyErrors.foreman}
            >
              <option value="">Select foreman area…</option>
              {foremanOptions.map((fa) => (
                <option key={fa.id} value={String(fa.id)}>
                  {fa.name}
                </option>
              ))}
            </Form.Select>
            {weeklyErrors.foreman && <Form.Control.Feedback type="invalid">{weeklyErrors.foreman}</Form.Control.Feedback>}
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group controlId="printWeeklyStart">
                <Form.Label className="tw-text-sm">Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={weeklyStart}
                  onChange={(e) => handleWeeklyStartChange(e.target.value)}
                  disabled={weeklyLoading}
                  isInvalid={!!weeklyErrors.start}
                />
                {weeklyErrors.start && <Form.Control.Feedback type="invalid">{weeklyErrors.start}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="printWeeklyEnd">
                <Form.Label className="tw-text-sm">End date</Form.Label>
                <Form.Control
                  type="date"
                  value={weeklyEnd}
                  onChange={(e) => handleWeeklyEndChange(e.target.value)}
                  disabled={weeklyLoading}
                  isInvalid={!!weeklyErrors.end}
                />
                {weeklyErrors.end && <Form.Control.Feedback type="invalid">{weeklyErrors.end}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}
    </ResponsiveModal>
  );
};

export default PrintScheduleModal;
